import React from "react";
import { Popover } from "antd";
import { Check, ChevronDown, Search } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

// Ghost toolbar dropdown for page filter bars.
//
// Why this exists instead of an Ant Select: a Select sizes itself to its
// container and absolutely positions its chevron at that container's edge, so
// the arrow floats far past a short label and never lines up with the plain
// `tg-tool` buttons beside it. Every control in a `.tg-filterbar` should be the
// same 28px button so the row can't drift.
export function ToolSelect({
  icon, value, options = [], onChange, placeholder = "Select",
  searchThreshold = 8, panelWidth = 236, placement = "bottomLeft",
  allowCustom = false,
}) {
  const t = useDashTheme();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");

  const current = options.find((o) => (o.value ?? null) === (value ?? null));
  const displayLabel = current?.label ?? (value ? String(value) : null);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => String(o.label).toLowerCase().includes(needle)
      || String(o.value).toLowerCase().includes(needle))
    : options;
  const customValue = q.trim();
  const showCustom = allowCustom && customValue
    && !options.some((o) => String(o.value).toLowerCase() === customValue.toLowerCase()
      || String(o.label).toLowerCase() === customValue.toLowerCase());
  const showSearch = allowCustom || options.length > searchThreshold;

  const toggle = (next) => { setOpen(next); if (!next) setQ(""); };

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={toggle}
      placement={placement}
      arrow={false}
      overlayInnerStyle={{
        padding: 6, background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
      }}
      content={
        <div style={{ width: panelWidth }}>
          {showSearch && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6, padding: "0 8px", marginBottom: 4,
              height: 30, borderRadius: t.r.sm, background: t.c.surfaceMuted,
            }}>
              <Search size={13} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
              <input
                autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={allowCustom ? "Search or type a site/app" : "Search"}
                aria-label="Filter options"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && showCustom) {
                    onChange(customValue);
                    toggle(false);
                  }
                }}
                style={{
                  flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                  color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family,
                }}
              />
            </div>
          )}
          <div style={{ maxHeight: 264, overflowY: "auto" }}>
            {filtered.length === 0 && !showCustom && (
              <div style={{ padding: "8px 10px", fontSize: t.font.size.md, color: t.c.textTertiary }}>
                Nothing matches “{q}”
              </div>
            )}
            {showCustom && (
              <button
                type="button"
                onClick={() => { onChange(customValue); toggle(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  height: 32, padding: "0 8px", border: "none", borderRadius: t.r.sm,
                  background: "transparent", cursor: "pointer",
                  color: t.c.primary, fontSize: t.font.size.md, fontFamily: t.font.family, textAlign: "left",
                }}
              >
                Use “{customValue}”
              </button>
            )}
            {filtered.map((o) => {
              const selected = (o.value ?? null) === (value ?? null);
              return (
                <button
                  key={String(o.value ?? "__all__")}
                  type="button"
                  onClick={() => { onChange(o.value ?? null); toggle(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    height: 32, padding: "0 8px", border: "none", borderRadius: t.r.sm,
                    background: selected ? t.c.surfaceMuted : "transparent", cursor: "pointer",
                    color: selected ? t.c.text : t.c.textSecondary,
                    fontSize: t.font.size.md, fontFamily: t.font.family, textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = selected ? t.c.surfaceMuted : "transparent"; }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.label}
                  </span>
                  {selected && <Check size={14} style={{ color: t.c.primary, flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>
      }
    >
      <button type="button" className={`tg-tool ${open ? "is-open" : ""}`}>
        {icon}
        <span className="tg-tool-value">{displayLabel ?? placeholder}</span>
        <ChevronDown size={13} />
      </button>
    </Popover>
  );
}
